/**
 * core-siege (C3) campaign graph. Hand-authored (and editable via the
 * /dev/siege-campaign-editor tool). Keyed by `map_key` from
 * `SIEGE_MAP_CATALOG`.
 *
 * `SIEGE_REGIONS`: named AABBs on the overworld (normalized 0-1).
 * `unlocked_by`: parent region ids — ALL must be fully cleared to unlock
 *                this region. Empty array = starter (`big_city`).
 * `region_pin`: normalized [0-1] position inside the node's region AABB.
 *
 * Once a region is unlocked, every map in it is playable (no map DAG).
 *
 * Graph rules enforced by the campaign editor:
 *   - Region unlock graph must be a DAG (no cycles).
 *   - At least one region must have `unlocked_by: []` (campaign entrance).
 *   - Regions must not overlap.
 */

export interface SiegeRegion {
  id: string;
  name: string;
  /** Normalized AABB on the world map image (0-1). */
  bounds: { x: number; y: number; w: number; h: number };
  /** Parent region ids — ALL must be cleared to unlock this region. */
  unlocked_by: string[];
}

export interface SiegeCampaignNode {
  map_key: string;
  region_id: string;
  /** Normalized [0-1] position inside the region AABB. */
  region_pin: { x: number; y: number };
}

export const SIEGE_REGIONS: SiegeRegion[] = [
  {
    id: "far_south",
    name: "Far South",
    bounds: { x: 0.2813, y: 0.7653, w: 0.2966, h: 0.1811 },
    unlocked_by: ["ruins"],
  },
  {
    id: "big_city",
    name: "Big City",
    bounds: { x: 0.7725, y: 0.2858, w: 0.2182, h: 0.2205 },
    unlocked_by: [],
  },
  {
    id: "temples",
    name: "Temples",
    bounds: { x: 0.5839, y: 0.5091, w: 0.3509, h: 0.2046 },
    unlocked_by: ["big_city"],
  },
  {
    id: "ruins",
    name: "Ruins",
    bounds: { x: 0.7538, y: 0.739, w: 0.227, h: 0.2374 },
    unlocked_by: ["temples"],
  },
  {
    id: "ice_caves",
    name: "Ice Caves",
    bounds: { x: 0.7045, y: 0.0475, w: 0.2845, h: 0.2336 },
    unlocked_by: ["big_city"],
  },
  {
    id: "plains_north",
    name: "Plains North",
    bounds: { x: 0.523, y: 0.2089, w: 0.1732, h: 0.29 },
    unlocked_by: ["big_city"],
  },
  {
    id: "frost_desert",
    name: "Frost Desert",
    bounds: { x: 0.4572, y: 0.0165, w: 0.2401, h: 0.1811 },
    unlocked_by: ["ice_caves"],
  },
  {
    id: "mountain",
    name: "Mountain",
    bounds: { x: 0.2588, y: 0.1, w: 0.1957, h: 0.3143 },
    unlocked_by: ["plains_north"],
  },
  {
    id: "plains_south",
    name: "Plains South",
    bounds: { x: 0.3509, y: 0.4238, w: 0.1667, h: 0.2674 },
    unlocked_by: ["temples", "plains_north"],
  },
  {
    id: "tropical_isle",
    name: "Tropical Isle",
    bounds: { x: 0.0532, y: 0.024, w: 0.2007, h: 0.2749 },
    unlocked_by: ["frost_desert", "mountain"],
  },
  {
    id: "broken_passage",
    name: "Broken Passage",
    bounds: { x: 0.1573, y: 0.3046, w: 0.0959, h: 0.2778 },
    unlocked_by: ["mountain", "plains_south"],
  },
  {
    id: "easter",
    name: "Easter",
    bounds: { x: 0.0121, y: 0.4463, w: 0.1393, h: 0.2862 },
    unlocked_by: ["broken_passage"],
  },
  {
    id: "underground_secrets",
    name: "Underground Secrets",
    bounds: { x: 0.0171, y: 0.764, w: 0.253, h: 0.2105 },
    unlocked_by: ["easter", "far_south"],
  },
  {
    id: "old_forge",
    name: "Old Forge",
    bounds: { x: 0.5863, y: 0.7406, w: 0.159, h: 0.2471 },
    unlocked_by: ["temples"],
  },
];

export const SIEGE_CAMPAIGN: SiegeCampaignNode[] = [
  {
    map_key: "normal_outpost",
    region_id: "big_city",
    region_pin: { x: 0.8292, y: 0.7245 },
  },
  {
    map_key: "grass_outpost",
    region_id: "big_city",
    region_pin: { x: 0.5873, y: 0.9877 },
  },
  {
    map_key: "water_outpost",
    region_id: "big_city",
    region_pin: { x: 0.2073, y: 0.2031 },
  },
  {
    map_key: "bug_outpost",
    region_id: "big_city",
    region_pin: { x: 0.2428, y: 0.6449 },
  },
  {
    map_key: "flying_outpost",
    region_id: "big_city",
    region_pin: { x: 0.7908, y: 0.1316 },
  },
  {
    map_key: "ground_outpost",
    region_id: "big_city",
    region_pin: { x: 0.047, y: 1 },
  },
  {
    map_key: "dark_outpost",
    region_id: "temples",
    region_pin: { x: 0.0698, y: 0.5563 },
  },
  {
    map_key: "ghost_outpost",
    region_id: "temples",
    region_pin: { x: 0.1762, y: 0.3279 },
  },
  {
    map_key: "fairy_outpost",
    region_id: "temples",
    region_pin: { x: 0.6956, y: 0.4699 },
  },
  {
    map_key: "fighting_outpost",
    region_id: "temples",
    region_pin: { x: 0.5399, y: 0.6455 },
  },
  {
    map_key: "poison_outpost",
    region_id: "temples",
    region_pin: { x: 0.3623, y: 0.1802 },
  },
  {
    map_key: "dragon_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.8419, y: 0.4727 },
  },
  {
    map_key: "electric_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.1836, y: 0.9308 },
  },
  {
    map_key: "fire_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.783, y: 0.8463 },
  },
  {
    map_key: "rock_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.5, y: 0.5881 },
  },
  {
    map_key: "steel_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.155, y: 0.5913 },
  },
  {
    map_key: "ice_outpost",
    region_id: "plains_north",
    region_pin: { x: 0.1502, y: 0.1089 },
  },
  {
    map_key: "psychic_outpost",
    region_id: "temples",
    region_pin: { x: 0.5446, y: 0.2694 },
  },
  {
    map_key: "water_outskirts",
    region_id: "ice_caves",
    region_pin: { x: 0.5133, y: 0.9634 },
  },
  {
    map_key: "rock_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.1374, y: 0.1933 },
  },
  {
    map_key: "normal_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.1085, y: 0.6514 },
  },
  {
    map_key: "bug_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.4238, y: 0.9731 },
  },
  {
    map_key: "electric_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.759, y: 0.7489 },
  },
  {
    map_key: "ground_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.8488, y: 0.1901 },
  },
  {
    map_key: "grass_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.4909, y: 0.5799 },
  },
  {
    map_key: "dark_outskirts",
    region_id: "ruins",
    region_pin: { x: 0.2487, y: 0.835 },
  },
  {
    map_key: "ghost_outskirts",
    region_id: "ruins",
    region_pin: { x: 0.066, y: 0.9796 },
  },
  {
    map_key: "fire_outskirts",
    region_id: "plains_south",
    region_pin: { x: 0.4924, y: 0.2258 },
  },
  {
    map_key: "bug_bastion",
    region_id: "mountain",
    region_pin: { x: 0.2194, y: 0.9584 },
  },
  {
    map_key: "electric_bastion",
    region_id: "mountain",
    region_pin: { x: 0.1034, y: 0.5767 },
  },
  {
    map_key: "flying_bastion",
    region_id: "mountain",
    region_pin: { x: 0.2209, y: 0.1348 },
  },
  {
    map_key: "rock_bastion",
    region_id: "mountain",
    region_pin: { x: 0.6434, y: 0.6904 },
  },
  {
    map_key: "ground_bastion",
    region_id: "mountain",
    region_pin: { x: 0.9255, y: 0.9779 },
  },
  {
    map_key: "psychic_bastion",
    region_id: "ruins",
    region_pin: { x: 0.6937, y: 0.7229 },
  },
  {
    map_key: "dark_bastion",
    region_id: "ruins",
    region_pin: { x: 0.9082, y: 0.5215 },
  },
  {
    map_key: "ice_bastion",
    region_id: "ice_caves",
    region_pin: { x: 0.6795, y: 0.3441 },
  },
  {
    map_key: "normal_bastion",
    region_id: "ice_caves",
    region_pin: { x: 0.3082, y: 0.6567 },
  },
  {
    map_key: "poison_bastion",
    region_id: "ice_caves",
    region_pin: { x: 0.0755, y: 0.5024 },
  },
  {
    map_key: "grass_bastion",
    region_id: "broken_passage",
    region_pin: { x: 0.5138, y: 0.2859 },
  },
  {
    map_key: "fairy_bastion",
    region_id: "broken_passage",
    region_pin: { x: 0.3872, y: 0.9617 },
  },
  {
    map_key: "bug_fortress",
    region_id: "far_south",
    region_pin: { x: 0.6434, y: 0.2718 },
  },
  {
    map_key: "flying_fortress",
    region_id: "far_south",
    region_pin: { x: 0.3129, y: 0.2478 },
  },
  {
    map_key: "water_fortress",
    region_id: "far_south",
    region_pin: { x: 0.9217, y: 0.6496 },
  },
  {
    map_key: "psychic_fortress",
    region_id: "ruins",
    region_pin: { x: 0.496, y: 0.5231 },
  },
  {
    map_key: "psychic_citadel",
    region_id: "ruins",
    region_pin: { x: 0.5824, y: 0.0585 },
  },
  {
    map_key: "ice_citadel",
    region_id: "frost_desert",
    region_pin: { x: 0.576, y: 0.3346 },
  },
  {
    map_key: "dragon_citadel",
    region_id: "frost_desert",
    region_pin: { x: 0.8856, y: 0.4423 },
  },
  {
    map_key: "steel_citadel",
    region_id: "frost_desert",
    region_pin: { x: 0.0575, y: 0.9592 },
  },
  {
    map_key: "ice_fortress",
    region_id: "frost_desert",
    region_pin: { x: 0.594, y: 0.9161 },
  },
  {
    map_key: "steel_fortress",
    region_id: "frost_desert",
    region_pin: { x: 0.2559, y: 0.9764 },
  },
  {
    map_key: "grass_fortress",
    region_id: "tropical_isle",
    region_pin: { x: 0.18, y: 0.5215 },
  },
  {
    map_key: "grass_citadel",
    region_id: "tropical_isle",
    region_pin: { x: 0.7615, y: 0.1933 },
  },
  {
    map_key: "water_citadel",
    region_id: "tropical_isle",
    region_pin: { x: 0.4922, y: 0.9731 },
  },
  {
    map_key: "flying_citadel",
    region_id: "tropical_isle",
    region_pin: { x: 0.2307, y: 0.1251 },
  },
  {
    map_key: "normal_fortress",
    region_id: "tropical_isle",
    region_pin: { x: 0.5, y: 0.5 },
  },
  {
    map_key: "fairy_citadel",
    region_id: "easter",
    region_pin: { x: 0.6658, y: 0.294 },
  },
  {
    map_key: "bug_citadel",
    region_id: "easter",
    region_pin: { x: 0.18, y: 0.4532 },
  },
  {
    map_key: "normal_citadel",
    region_id: "easter",
    region_pin: { x: 0.8415, y: 0.7895 },
  },
  {
    map_key: "ghost_fortress",
    region_id: "underground_secrets",
    region_pin: { x: 0.8229, y: 0.2115 },
  },
  {
    map_key: "ghost_citadel",
    region_id: "underground_secrets",
    region_pin: { x: 0.8248, y: 0.8479 },
  },
  {
    map_key: "dark_citadel",
    region_id: "underground_secrets",
    region_pin: { x: 0.1534, y: 0.8968 },
  },
  {
    map_key: "poison_citadel",
    region_id: "underground_secrets",
    region_pin: { x: 0.4953, y: 0.2193 },
  },
  {
    map_key: "poison_fortress",
    region_id: "underground_secrets",
    region_pin: { x: 0.1496, y: 0.2329 },
  },
  {
    map_key: "dark_fortress",
    region_id: "underground_secrets",
    region_pin: { x: 0.5076, y: 0.887 },
  },
  {
    map_key: "dragon_fortress",
    region_id: "frost_desert",
    region_pin: { x: 0.9093, y: 0.942 },
  },
  {
    map_key: "rock_citadel",
    region_id: "mountain",
    region_pin: { x: 0.8279, y: 0.1982 },
  },
  {
    map_key: "rock_fortress",
    region_id: "mountain",
    region_pin: { x: 0.4756, y: 0.4451 },
  },
  {
    map_key: "water_bastion",
    region_id: "far_south",
    region_pin: { x: 0.708, y: 0.9209 },
  },
  {
    map_key: "ground_fortress",
    region_id: "ice_caves",
    region_pin: { x: 0.0717, y: 0.9475 },
  },
  {
    map_key: "fairy_fortress",
    region_id: "easter",
    region_pin: { x: 0.4844, y: 0.6141 },
  },
  {
    map_key: "electric_fortress",
    region_id: "old_forge",
    region_pin: { x: 0.0587, y: 0.9211 },
  },
  {
    map_key: "fighting_fortress",
    region_id: "old_forge",
    region_pin: { x: 0.0912, y: 0.1998 },
  },
  {
    map_key: "fire_fortress",
    region_id: "old_forge",
    region_pin: { x: 0.9516, y: 0.8853 },
  },
  {
    map_key: "electric_citadel",
    region_id: "old_forge",
    region_pin: { x: 0.503, y: 1 },
  },
  {
    map_key: "fighting_citadel",
    region_id: "old_forge",
    region_pin: { x: 0.5118, y: 0.0601 },
  },
  {
    map_key: "fire_citadel",
    region_id: "old_forge",
    region_pin: { x: 0.5162, y: 0.5426 },
  },
  {
    map_key: "ground_citadel",
    region_id: "old_forge",
    region_pin: { x: 1, y: 0.1901 },
  },
];
